import {FC, PropsWithChildren} from "react"

import {activeTheme} from "@frontend/themes/activeTheme"
import {css, cx} from "@styled-system/css"
import {hstack, vstack} from "@styled-system/patterns"
import {token} from "@styled-system/tokens"

const messageIconClass = css({display: "block", flexShrink: 0})

/** Same mark as the widget greeting icon in `ChatPanelBody.tsx`. */
const RiMessageIcon: FC = () => {
  if (activeTheme.capabilities.chatGreetingIcon === "emblemImage") {
    return (
      <img
        src={activeTheme.assets.chatEmblem}
        alt=""
        width={24}
        height={27}
        aria-hidden
        className={messageIconClass}
      />
    )
  }

  return (
    <svg
      width="24"
      height="21"
      viewBox="0 0 24 21"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
      className={messageIconClass}
    >
      <path
        d="M18 0H6L0 10.4993L6 21H18L24 10.4993L18 0Z"
        fill={token("colors.brandAccent.600")}
      />
      <path
        d="M11.9998 2.84763L11.7521 3.54871C10.6483 6.67414 8.2138 9.13517 5.12041 10.2491L4.42651 10.4994L5.12041 10.7497C8.2138 11.8648 10.6496 14.3246 11.7521 17.45L11.9998 18.1511L12.2475 17.45C13.3513 14.3246 15.7858 11.8635 18.8792 10.7497L19.5731 10.4994L18.8792 10.2491C15.7858 9.13386 13.35 6.67414 12.2475 3.54871L11.9998 2.84763Z"
        fill="white"
      />
    </svg>
  )
}
type Props = {
  className?: string
} & (
  | {
      isThinkingOrAI: true
      isInitialPage: true
      timestamp?: never
    }
  | {
      isThinkingOrAI: true
      timestamp?: Date
      isInitialPage?: never
    }
  | {
      isThinkingOrAI: false
      isInitialPage?: false
      timestamp?: Date
    }
)

export const messageContainerClass = "msg-container"

export const EventLayout: FC<PropsWithChildren<Props>> = (props) => {
  if (props.isInitialPage && props.isThinkingOrAI) {
    return (
      <AIEventLayout
        className={props.className}
        isInitialPage={props.isInitialPage}
      >
        {props.children}
      </AIEventLayout>
    )
  }

  return props.isThinkingOrAI ? (
    <AIEventLayout
      className={props.className}
      isInitialPage={props.isInitialPage}
    >
      {props.children}
    </AIEventLayout>
  ) : (
    <UserEventLayout className={props.className}>
      {props.children}
    </UserEventLayout>
  )
}

interface UserEventLayoutProps {
  className?: string
}

const UserEventLayout: FC<PropsWithChildren<UserEventLayoutProps>> = ({
  className,
  children,
}) => (
  <div
    className={`${vstack({
      gap: 6,
      alignItems: "flex-end",
    })} ${messageContainerClass}`}
  >
    <div
      className={cx(
        css({
          backgroundColor: "background.primary",
          borderRadius: "24px 24px 4px 24px",
          paddingX: 12,
          paddingY: 8,
          border: "1px token(colors.border.brand.primary) solid",
          width: "fit-content",
          marginLeft: "auto",
        }),
        className,
      )}
    >
      <div
        className={css({
          display: "flex",
          flexDirection: "column",
          gap: 16,
          desktop: {
            gap: 32,
            flexDirection: "row",
          },
          alignItems: "flex-start",
        })}
      >
        {children}
      </div>
    </div>
  </div>
)

interface AIEventLayoutProps {
  className?: string
  isInitialPage?: boolean
}

const AIEventLayout: FC<PropsWithChildren<AIEventLayoutProps>> = ({
  className,
  children,
  isInitialPage,
}) => (
  <div
    className={`${cx(
      css({
        display: "flex",
        flexDirection: {base: "column", desktop: "row"},
        alignItems: {base: "stretch", desktop: "flex-start"},
        gap: 8,
        desktopOnly: {
          padding: "16px 26px 16px 0px",
        },
        padding: "16px 8px 16px 0px",
      }),
      className,
    )} ${messageContainerClass}`}
  >
    {!isInitialPage && (
      <div
        className={hstack({
          gap: 16,
        })}
      >
        <RiMessageIcon />
      </div>
    )}
    <div
      className={vstack({
        alignItems: "stretch",
        gap: 12,
        minWidth: 0,
        flexGrow: 1,
        paddingY: 3,
      })}
    >
      <div
        className={css({
          backgroundColor: "background.primary",
          borderRadius: "4px 24px 24px 24px",
          paddingX: 12,
          paddingY: 8,
          border: "1px solid token(colors.border.secondary.200)",
          width: "fit-content",
          maxWidth: "100%",
        })}
      >
        {children}
      </div>
    </div>
  </div>
)
